import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { database } from '@/firebase';
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  orderBy,
} from 'firebase/firestore';
import { useAuthStore } from './auth';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase';

export const useStoryStore = defineStore('story', () => {
  const step = ref(1);

  const selectedGenre = ref([]);
  const selectedAudience = ref('adult');
  const selectedGender = ref('male');
  const selectedLanguage = ref('english');

  const generatedText = ref('');
  const generatedStory = ref('');
  const isGenerating = ref(false);
  const error = ref(null);

  const stories = ref([]);
  const isLoadingHistory = ref(false);

  const options = computed(() => ({
    genre: selectedGenre.value,
    audience: selectedAudience.value,
    gender: selectedGender.value,
    language: selectedLanguage.value,
  }));

  function buildPromptText() {
    let text = `Write a story for ${selectedAudience.value} ${selectedGender.value} in ${selectedLanguage.value}.`;
    if (selectedGenre.value.length > 0) {
      text += ` The genre of the story is ${selectedGenre.value.join(', ')}.`;
    }
    generatedText.value = text;
  }

  async function generateStory() {
    error.value = null;
    isGenerating.value = true;

    if (!generatedText.value) {
      buildPromptText();
    }

    try {
      // The Gemini key lives server-side in a Firebase Cloud Function.
      // The client never sees it; model selection and retries happen there.
      const callGenerateStory = httpsCallable(functions, 'generateStory');
      const { data } = await callGenerateStory({ prompt: generatedText.value });
      const text = (data?.story || '').trim();
      if (!text) {
        error.value =
          'The story service returned an empty response. Please retry.';
        return;
      }
      generatedStory.value = text;
    } catch (e) {
      // HttpsError surfaces a user-safe message; fall back to a generic one.
      error.value = e?.message || 'Failed to generate story';
    } finally {
      isGenerating.value = false;
    }
  }

  async function saveStory(title) {
    const authStore = useAuthStore();
    const data = {
      user_id: authStore.user.uid,
      title,
      selectedGenre: selectedGenre.value,
      selectedAudiance: selectedAudience.value,
      selectedGender: selectedGender.value,
      selectedLanguage: selectedLanguage.value,
      generatedText: generatedText.value,
      story: generatedStory.value,
      timestamp: Date.now(),
    };
    await addDoc(collection(database, 'userStories'), data);
  }

  async function loadStories() {
    const authStore = useAuthStore();
    if (!authStore.user) return;
    isLoadingHistory.value = true;
    try {
      const q = query(
        collection(database, 'userStories'),
        where('user_id', '==', authStore.user.uid),
        orderBy('timestamp', 'desc')
      );
      const snapshot = await getDocs(q);
      stories.value = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (e) {
      error.value = e.message || 'Failed to load stories';
    } finally {
      isLoadingHistory.value = false;
    }
  }

  function resetWizard() {
    step.value = 1;
    selectedGenre.value = [];
    selectedAudience.value = 'adult';
    selectedGender.value = 'male';
    selectedLanguage.value = 'english';
    generatedText.value = '';
    generatedStory.value = '';
    error.value = null;
  }

  return {
    step,
    selectedGenre,
    selectedAudience,
    selectedGender,
    selectedLanguage,
    generatedText,
    generatedStory,
    isGenerating,
    error,
    stories,
    isLoadingHistory,
    options,
    buildPromptText,
    generateStory,
    saveStory,
    loadStories,
    resetWizard,
  };
});
